import { Check } from "lucide-react"
import { cn } from "~/common/cn"
import { podTiers, type PodTierId } from "~/content/pods"
import { ScheduleLink } from "~/marketing/schedule-link"

const rows: { label: string; values: Record<PodTierId, string | boolean> }[] = [
  {
    label: "Product meetings",
    values: { low: "2 / month", medium: "1 / week", high: "2 / week", max: "Agreed together" },
  },
  {
    label: "Build & feedback",
    values: {
      low: "2 rounds / month",
      medium: "Weekly batches",
      high: "2 parallel tracks",
      max: "Multiple teams",
    },
  },
  {
    label: "Monthly security review",
    values: { low: true, medium: true, high: true, max: true },
  },
  ...[
    "UI/UX design",
    "Development",
    "Product management",
    "QA testing",
    "Standups & shared Slack",
    "You own the code",
  ].map((label) => ({
    label,
    values: { low: true, medium: true, high: true, max: true },
  })),
]

export const PodComparison = ({ selected }: { selected: PodTierId }) => (
  <section
    className="marketing-surface-dark marketing-section text-secondary-foreground"
    aria-labelledby="pod-comparison-heading"
  >
    <div className="container">
      <h2 id="pod-comparison-heading" className="home-sans-heading marketing-section-heading">
        <span className="home-emphasis">Choose your pace.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
        Compare what comes with each level.
      </p>
      <p id="pod-comparison-scroll-hint" className="mt-4 text-sm font-semibold lg:hidden">
        Swipe or scroll to compare levels →
      </p>
      <div
        className="pod-comparison-scroll mt-8 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        role="region"
        aria-label="Compare pod effort levels"
        aria-describedby="pod-comparison-notes"
        tabIndex={0}
      >
        <table className="pod-comparison-table text-foreground">
          <caption className="sr-only">Pod pricing, delivery pace, and included services</caption>
          <thead>
            <tr>
              <th scope="col" className="pod-comparison-label text-left">
                Your team,
                <br />
                your pace.
              </th>
              {podTiers.map((tier) => (
                <th
                  key={tier.id}
                  scope="col"
                  data-pod-tier={tier.id}
                  className={cn("py-8", selected === tier.id && "pod-comparison-selected")}
                >
                  <span className="block text-2xl font-semibold">{tier.name}</span>
                  <span className="mt-3 block text-xl font-semibold">
                    {tier.price}
                    {tier.id !== "max" && <span className="text-base font-normal"> / month</span>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row" className="pod-comparison-label text-left font-semibold">
                  {row.label}
                </th>
                {podTiers.map((tier) => {
                  const value = row.values[tier.id]
                  return (
                    <td key={tier.id}>
                      {typeof value === "boolean" ? (
                        value ? (
                          <>
                            <Check aria-hidden="true" className="mx-auto size-6 text-primary" />
                            <span className="sr-only">Included</span>
                          </>
                        ) : (
                          <>
                            <span aria-hidden="true">—</span>
                            <span className="sr-only">Not included</span>
                          </>
                        )
                      ) : (
                        value
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" className="pod-comparison-label">
                <span className="sr-only">Get started</span>
              </th>
              {podTiers.map((tier) => (
                <td key={tier.id}>
                  <ScheduleLink
                    ctaLocation={tier.ctaLocation}
                    ariaLabel={`${tier.cta.replace(" →", "")} with the ${tier.name} pod`}
                    className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-full bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    {tier.cta}
                  </ScheduleLink>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div id="pod-comparison-notes" className="mt-7 space-y-3 text-secondary-foreground/80">
        <p className="font-semibold text-secondary-foreground">
          All levels are month to month. Change effort or stop at the end of any month.
        </p>
      </div>
    </div>
  </section>
)
